import React, { useState, useMemo } from 'react';
import { Button, Typography, Box, Card, Stack, TextField, Checkbox, FormControlLabel, useTheme } from '@mui/material';
import { useBreakpointDown } from '@/hooks/use-breakpoint';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import { useTranslations } from '@/i18n/use-translations';
import { useNotifications } from '@/hooks/use-notifications';
import { useGetQuestionsQuery } from '@/state/queries/users/use-get-questions';
import Modal from '@/components/common/modal';
import DynamicMuiIcon from '@/components/common/dynamic-mui-icon';
import { isValidIconName, type IconName } from '@/components/common/dynamic-mui-icon.constants';
import type { z } from 'zod';
import type { QuestionDto } from '@/types/dtos/user/response/question.dto';
import type { QuestionAnswerDto } from '@/types/dtos/user/request/question-answer.dto';
import { QuestionContext } from '@/enums/question-context.enum';
import { userOnboardingSchema } from '@/schemas/user/onboarding-schema';

type FormData = z.infer<typeof userOnboardingSchema>;

interface UserQuestionaireFormsProps {
  formData: FormData;
  onComplete: (payload: { skipOrg: boolean; questionAnswers: QuestionAnswerDto[] }) => void;
  onBack: () => void;
  activeStep: number;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  answersMap: Record<string, string[]>;
  setAnswersMap: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  commentsMap: Record<string, Record<string, string>>;
  setCommentsMap: React.Dispatch<React.SetStateAction<Record<string, Record<string, string>>>>;
}

const UserQuestionaireForms: React.FC<UserQuestionaireFormsProps> = ({
  formData,
  onComplete,
  onBack,
  activeStep,
  setActiveStep,
  answersMap,
  setAnswersMap,
  commentsMap,
  setCommentsMap,
}) => {
  const { data: allQuestions } = useGetQuestionsQuery();
  const t = useTranslations();
  const notify = useNotifications();
  const theme = useTheme();

  const [open, setOpen] = useState(true);
  const isMobile = useBreakpointDown('sm');
  const currentLang = formData.language || 'en';

  const roleQuestion = allQuestions?.find((q) => (q.context ?? '').toLowerCase() === QuestionContext.Role);
  const serviceQuestion = allQuestions?.find((q) => (q.context ?? '').toLowerCase() === QuestionContext.Services);

  const selectedRoleAnswerIds = roleQuestion ? (answersMap[roleQuestion.id] ?? []) : [];

  const serviceAnswer = roleQuestion?.answers.find((a) => a.orderInQuestion === 7);
  const serviceSelected = !!serviceAnswer && selectedRoleAnswerIds.includes(serviceAnswer.id);

  const steps = useMemo(() => {
    if (!allQuestions) return [];
    let result: QuestionDto[] = [];
    if (roleQuestion) result.push(roleQuestion);
    if (serviceSelected && serviceQuestion) result.push(serviceQuestion);
    const remaining = allQuestions.filter((q) => q.id !== roleQuestion?.id && q.id !== serviceQuestion?.id);
    result = result.concat(remaining);
    return result;
  }, [allQuestions, serviceSelected, roleQuestion, serviceQuestion]);

  const currentQuestion = steps[activeStep];
  const isLastStep = activeStep === steps.length - 1;

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    } else {
      onBack();
    }
  };

  const toggleAnswer = (questionId: string, answerId: string) => {
    setAnswersMap((prev) => {
      const current = prev[questionId] ?? [];
      const updated = current.includes(answerId) ? current.filter((id) => id !== answerId) : [...current, answerId];

      if (current.includes(answerId)) {
        setCommentsMap((prevComments) => {
          const newMap = { ...prevComments };
          if (newMap[questionId]) {
            delete newMap[questionId][answerId];
          }
          return newMap;
        });
      }

      return { ...prev, [questionId]: updated };
    });
  };

  const handleCommentChange = (questionId: string, answerId: string, comment: string) => {
    setCommentsMap((prev) => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] || {}),
        [answerId]: comment,
      },
    }));
  };

  const getSelectedRoleKeys = (questions: QuestionDto[] | undefined, map: Record<string, string[]>): Set<string> => {
    if (!questions) return new Set<string>();
    const rq = questions.find((q) => (q.context ?? '').toLowerCase() === QuestionContext.Role);
    if (!rq) return new Set<string>();
    const selectedIds = new Set(map[rq.id] ?? []);
    const keys = new Set<string>();
    for (const a of rq.answers) {
      if (selectedIds.has(a.id)) {
        const keyCandidate =
          ('key' in a ? (a as { key?: string }).key : undefined) ??
          a.translations?.en?.title ??
          Object.values(a.translations ?? {})[0]?.title;

        if (keyCandidate) keys.add(String(keyCandidate).trim().toLowerCase());
      }
    }
    return keys;
  };

  const buildQuestionAnswers = (
    questions: QuestionDto[] | undefined,
    map: Record<string, string[]>,
    comments: Record<string, Record<string, string>>,
  ): QuestionAnswerDto[] => {
    const result: QuestionAnswerDto[] = [];
    for (const q of questions ?? []) {
      for (const answerId of map[q.id] ?? []) {
        const ans = q.answers.find((a) => a.id === answerId);
        const userComment = ans?.commentRequired ? comments[q.id]?.[answerId] : undefined;
        result.push({
          questionId: q.id,
          answerId,
          value: true,
          userComment,
          updatedAt: new Date().toISOString(),
        });
      }
    }
    return result;
  };

  const handleNext = () => {
    if (!currentQuestion?.id) return;

    const selected = answersMap[currentQuestion.id] ?? [];
    if (selected.length === 0) {
      notify.show(t('onBoarding.notifications.select'), {
        severity: 'warning',
      });
      return;
    }

    if (isLastStep) {
      const selectedRoleKeys = getSelectedRoleKeys(allQuestions, answersMap);

      const allowed = new Set<string>([
        QuestionContext.Moderator.toLowerCase(),
        QuestionContext.Speaker.toLowerCase(),
        QuestionContext.CrewMember.toLowerCase(),
      ]);

      const hasAny = selectedRoleKeys.size > 0;
      const isSubsetOfAllowed = hasAny && [...selectedRoleKeys].every((k) => allowed.has(k));

      const skipOrg = isSubsetOfAllowed;

      const questionAnswers = buildQuestionAnswers(allQuestions, answersMap, commentsMap);

      setOpen(false);
      onComplete({ skipOrg, questionAnswers });
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const renderQuestionForm = (question: QuestionDto) => {
    const selectedAnswers = answersMap[question.id] ?? [];
    const isRole = (question.context ?? '').toLowerCase() === QuestionContext.Role;

    return (
      <Box>
        {isRole ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            {question.answers.map((role) => {
              const selected = selectedAnswers.includes(role.id);
              const title = role.translations?.[currentLang]?.title || '';
              return (
                <Box key={role.id} sx={{ width: 'calc(50% - 8px)', minWidth: '160px' }}>
                  <Card
                    onClick={() => toggleAnswer(question.id, role.id)}
                    sx={{
                      'width': '100%',
                      'height': 100,
                      'textAlign': 'center',
                      'cursor': 'pointer',
                      'border': `2px solid ${role.color}`,
                      'backgroundColor': selected ? role.color + '40' : 'transparent',
                      'boxShadow': selected ? 5 : 1,
                      'transform': selected ? 'scale(1.02)' : 'scale(1)',
                      'transition': 'all 0.2s ease-in-out',
                      'display': 'flex',
                      'flexDirection': 'column',
                      'justifyContent': 'center',
                      'alignItems': 'center',
                      '&:hover': { boxShadow: 6 },
                    }}
                    variant="outlined"
                  >
                    <Box sx={{ width: 48, height: 48, mb: 1 }}>
                      <DynamicMuiIcon
                        iconName={isValidIconName(role.icon ?? '') ? (role.icon as IconName) : 'HelpOutline'}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ px: 0.5, wordBreak: 'break-word' }}>
                      {title}
                    </Typography>
                  </Card>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Stack spacing={2} mt={-2}>
            {question.answers.map((ans) => {
              const isChecked = selectedAnswers.includes(ans.id);
              const title = ans.translations?.[currentLang]?.title || '';
              const isOther = ans.commentRequired === true;

              return (
                <Box key={ans.id} sx={{ mb: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isChecked}
                        onChange={() => toggleAnswer(question.id, ans.id)}
                        sx={{ color: isChecked ? theme.palette.primary.main : undefined }}
                      />
                    }
                    label={title}
                  />
                  {isOther && isChecked && (
                    <TextField
                      fullWidth
                      size="small"
                      variant="standard"
                      placeholder={t('onBoarding.placeHolder')}
                      label={t('onBoarding.label')}
                      value={commentsMap[question.id]?.[ans.id] || ''}
                      onChange={(e) => handleCommentChange(question.id, ans.id, e.target.value)}
                      sx={{ mt: 2 }}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    );
  };

  return (
    <Modal
      open={open}
      handleClose={() => {}}
      hideCloseIcon
      title={`${currentQuestion?.translations?.[currentLang]?.title ?? ''} ${formData.firstName}.`}
      description={currentQuestion?.translations?.[currentLang]?.subtitle ?? ''}
      size="md"
      fullScreen={isMobile}
      width={isMobile ? undefined : '450px'}
      height={isMobile ? undefined : '650px'}
      footer={
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Button variant="outlined" color="secondary" onClick={handleBack} startIcon={<ChevronLeftOutlinedIcon />}>
            {t('common.back')}
          </Button>
          <Button variant="contained" color="info" onClick={handleNext}>
            {t('onBoarding.continue')}
          </Button>
        </Box>
      }
    >
      <Box sx={{ padding: 2, maxHeight: '100%', overflow: 'auto' }}>
        {currentQuestion && renderQuestionForm(currentQuestion)}
      </Box>
    </Modal>
  );
};

export default UserQuestionaireForms;
